import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IconText } from '~/src/dash/components/IconText';
import { SimpleTabs } from '~/src/dash/components/tabs/SimpleTabs';
import { SimpleTabSections } from '~/src/dash/components/tabs/SimpleTabSections';
import { ButtonV2, Col, Cols, Panel, PanelBreak, Panels, spacing } from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  RoutePlatformConnectOfferNew,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED, IS_AUTHORIZED_OR } from '~/src/dash/utils/permission_service';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { EPublisherType } from '~/src/dash/views/platform-connect/common';
import { IPlatformConnectConfig } from '~/src/dash/views/platform-connect/config';
import { ConnectionsContainer } from '~/src/dash/views/platform-connect/Connections/container';
import { PlatformConnectsContext } from '~/src/dash/views/platform-connect/contexts';
import { PendingOffersContainer } from '~/src/dash/views/platform-connect/PendingOffers/container';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

export const PlatformConnectView = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [key, setKey] = useState(0);
  const [tabSections] = useState<(() => JSX.Element)[]>([
    ConnectionsContainer,
    PendingOffersContainer,
  ]);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { session, user, isSuperUser } = useBaseStore();
  const { PlatformConnect } = useBaseConfig();
  const cfg = PlatformConnect.Main as IPlatformConnectConfig;

  // when user clicks on a tab
  const handleChangeTab = (_event: React.SyntheticEvent, index: number) => {
    setActiveTabIndex(index);
  };

  const forceUpdate = (tabIndex: number) => {
    unstable_batchedUpdates(() => {
      setActiveTabIndex(tabIndex);
      setKey((value) => value + 1);
    });
  };

  // route to create a new platform connection
  const navigateToPlatformConnectOfferNewView = () => {
    track('PC - Draft New Offer', { is_super_user: isSuperUser }, user.user);
    navigate(RoutePlatformConnectOfferNew, extractRouteParams({ session, user }));
  };

  return (
    <PlatformConnectsContext.Provider
      value={{
        cfg,
        // TODO: change auth verifications
        readAuthorization: IS_AUTHORIZED_OR(
          ['read_manage_buyer_requests', 'read_manage_seller_requests'],
          user.user,
        ),
        writeAuthorization: IS_AUTHORIZED_OR(
          ['write_manage_buyer_requests', 'write_manage_seller_requests'],
          user.user,
        ),
        forceUpdate,
      }}
    >
      <Panels max vertical relative minWidthMain>
        <Panel largeHorizontalPadding verticalPadding>
          <TopBar title='Platform Connect Beta' />
        </Panel>
        {/* NEW PLATFORM CONNECTION */}
        <Panel largeHorizontalPadding>
          <Cols
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Col>
              <SimpleTabs
                activeTabIndex={activeTabIndex}
                ariaLabel='Platform Connect'
                handleChangeTab={handleChangeTab}
                tabs={cfg.tabs}
              />
            </Col>
            <Col>
              <Cols
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {(session.publisher.PublisherType === EPublisherType.PUBLISHER_TYPE_BUYER ||
                  session.publisher.PublisherType === EPublisherType.PUBLISHER_TYPE_BOTH) && (
                  <Col>
                    <ButtonV2
                      onClick={navigateToPlatformConnectOfferNewView}
                      disabled={
                        !IS_AUTHORIZED(['write_manage_seller_requests'], user.user).isAuthorized
                      }
                      style={{
                        padding: '8px',
                      }}
                    >
                      <IconText
                        bold
                        gap={spacing.extraSmall}
                        color={publicaColors.white}
                        icon='flag'
                        text='Draft New Offer'
                        style={{
                          padding: 'none',
                        }}
                      />
                    </ButtonV2>
                  </Col>
                )}
              </Cols>
            </Col>
          </Cols>
        </Panel>
        <Panel scroll>
          <SimpleTabSections key={key} activeTabIndex={activeTabIndex} sections={tabSections} />
        </Panel>
        <PanelBreak height={spacing.large} />
        <SnackbarView open={!!snackbarMessage} error={null} onClose={() => setSnackbarMessage('')}>
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </PlatformConnectsContext.Provider>
  );
};
